/**
 * Utility for determining whether a timeline cell interval falls within
 * a highlighted allocation's date range.
 *
 * Used by the human-resource highlight feature to determine which cells
 * in a row should receive visual highlighting.
 */

/**
 * Checks whether a cell's time interval overlaps with a highlight date range.
 *
 * @param cellIntervalStart - Start of the cell's time interval
 * @param cellIntervalEnd - End of the cell's time interval
 * @param startDate - ISO 8601 start date of the highlight range (undefined = unbounded start)
 * @param endDate - ISO 8601 end date of the highlight range (undefined = unbounded end)
 * @returns true if the cell interval overlaps with the highlight range
 *
 * Behavior:
 * - No startDate and no endDate: returns true (no date range = highlight entire row)
 * - startDate but no endDate: returns cellIntervalEnd >= startDate (open-ended forward)
 * - No startDate but endDate: returns cellIntervalStart <= endDate (open-ended backward)
 * - Both: standard interval overlap (cellIntervalStart <= endDate AND cellIntervalEnd >= startDate)
 */
export function isIntervalInHighlightRange(
  cellIntervalStart: Date,
  cellIntervalEnd: Date,
  startDate?: string,
  endDate?: string,
): boolean {
  // No date range means highlight the entire row
  if (!startDate && !endDate) {
    return true;
  }

  const hasStart = !!startDate;
  const hasEnd = !!endDate;

  if (hasStart && !hasEnd) {
    // Open-ended forward: cell must end on or after the start date
    return cellIntervalEnd >= new Date(startDate!);
  }

  if (!hasStart && hasEnd) {
    // Open-ended backward: cell must start on or before the end date
    return cellIntervalStart <= new Date(endDate!);
  }

  // Both dates present: standard interval overlap
  return cellIntervalStart <= new Date(endDate!) && cellIntervalEnd >= new Date(startDate!);
}
